import { useEffect, useState } from "react";
import { TableProps, Tag, notification, Breadcrumb } from "antd";
import {
  getColumnSearchProps,
  getColumnSelectProps,
} from "../../../../shared/base-components/Filter/Filters";
import {
  Deliverable,
  DeliverableFilter,
  DeliverablesFilterParams,
} from "./DeliverableList.types";
import {
  DEFAULT_GUID,
  DELIVERABLE_NAME_LABEL,
  FILTER_CREATED_BY_LABEL,
  FILTER_CREATED_ON_LABEL,
  FILTER_TAGS_LABEL,
  GLOBAL_TEMPLATE_LABEL,
  TOAST_DEFAULT_ERROR_MESSAGE,
} from "../../../../shared/utils/CommonConstants";
import DeliverableService from "../../services/DeliverableService";
import { ACTIVE_TABS_KEY, NAME_KEY, MULTI_SHEET_KEY } from "../../../../shared/utils/CommonConstants";
import { DeliverableMenuSheet } from "../../../../shared/screen-components/DeliverableMenuComponent/DeliverableMenuComponent.types";

import "./DeliverableList.css";
import { useNavigate } from "react-router-dom";
import { Container } from "../../../../shared/base-components/Layouts/Container/Container.styles";
import { PagedResponse } from "../../../../shared/types/PagedResponse";
import { Flex } from "../../../../shared/base-components/Layouts/Flex/Flex.styles";
import { SearchInput } from "../../../../shared/base-components/Search/Search";
import { Table } from "../../../../shared/base-components/Table/Table";
import { DeliverableCreate } from "../../../../shared/screen-components/DeliverableCreateComponent/DeliverableCreateComponent";

export const DeliverableList: React.FC = () => {
  const navigate = useNavigate();

  const deliverableService = new DeliverableService();

  const [isLoading, setIsLoading] = useState(false);
  const [deliverables, setDeliverables] = useState<Deliverable[]>([]);

  const [filterOptions, setFilterOptions] = useState<DeliverableFilter>({
    ruleEngineTemplateList: [],
    coaTemplateList: [],
    tagValueList: [],
    createdUserList: [],
  });
  const [tagOptions, setTagOptions] = useState<any>([]);
  const [tags, setTags] = useState<any>([]);

  const [total, setTotal] = useState(0);

  const [nameFilter, setNameFilter] = useState<string | null>(null);
  const [ruleEngineTemplateNameFilter, setRuleEngineTemplateNameFilter] =
    useState<number | null>(null);
  const [coaTemplateNameFilter, setCoaTemplateNameFilter] = useState<
    number | null
  >(null);
  const [tagsFilter, setTagsFilter] = useState<string | null>(null);
  const [createdOnFilter, setCreatedOnFilter] = useState<string | null>(null);
  const [createdUserNameFilter, setCreatedUserNameFilter] = useState<
    string | null
  >(null);
  const [searchValue, setSearchValue] = useState<string>("");

  useEffect(() => {
    fetchFilterOptions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hasFilters = [
      nameFilter,
      ruleEngineTemplateNameFilter,
      coaTemplateNameFilter,
      tagsFilter,
      createdOnFilter,
      createdUserNameFilter,
      searchValue,
    ].some((filter) => filter !== "" || filter !== null);

    if (hasFilters) {
      fetchData(1, 10);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    nameFilter,
    ruleEngineTemplateNameFilter,
    coaTemplateNameFilter,
    tagsFilter,
    createdOnFilter,
    createdUserNameFilter,
    searchValue,
  ]);

  const columns: TableProps["columns"] = [
    {
      title: DELIVERABLE_NAME_LABEL,
      dataIndex: "name",
      ...getColumnSearchProps(
        "name",
        DELIVERABLE_NAME_LABEL,
        nameFilter,
        setNameFilter
      ),
      width: "18%",
    },
    {
      title: GLOBAL_TEMPLATE_LABEL,
      dataIndex: "ruleEngineTemplateName",
      ...getColumnSelectProps(
        "ruleEngineTemplateName",
        GLOBAL_TEMPLATE_LABEL,
        filterOptions.ruleEngineTemplateList.map((t) => {
          return { value: t.id, label: t.templateName };
        }),
        ruleEngineTemplateNameFilter,
        setRuleEngineTemplateNameFilter,
        !!ruleEngineTemplateNameFilter
      ),
      width: "15%",
    },
    {
      title: FILTER_CREATED_BY_LABEL,
      dataIndex: "createdUserName",
      ...getColumnSelectProps(
        "createdUserName",
        FILTER_CREATED_BY_LABEL,
        filterOptions.createdUserList.map((t) => {
          return { value: t.id, label: t.userName };
        }),
        createdUserNameFilter,
        setCreatedUserNameFilter,
        !!createdUserNameFilter
      ),
      width: "15%",
    },
    {
      title: FILTER_CREATED_ON_LABEL,
      dataIndex: "createdOn",
      ...getColumnSearchProps(
        "createdOn",
        FILTER_CREATED_ON_LABEL,
        createdOnFilter,
        setCreatedOnFilter,
        true
      ),
      width: "15%",
    },
    {
      title: FILTER_TAGS_LABEL,
      dataIndex: "tags",
      ...getColumnSelectProps(
        "tags",
        FILTER_TAGS_LABEL,
        filterOptions.tagValueList.map((t) => {
          return { value: t.value, label: t.value };
        }),
        tagsFilter,
        setTagsFilter,
        !!tagsFilter
      ),
      render: (_, { tag: tags }) => (
        <>
          {tags.length > 0
            ? tags.map((tag: any) => {
                let color = tag.name.length > 7 ? "geekblue" : "green";
                return (
                  <Tag color={color} key={tag.id}>
                    {tag.name}: {tag.value}
                  </Tag>
                );
              })
            : "-"}
        </>
      ),
    },
  ];

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const clearFilter = () => {
    if (
      !!nameFilter ||
      !!ruleEngineTemplateNameFilter ||
      !!coaTemplateNameFilter ||
      !!createdOnFilter ||
      !!createdUserNameFilter ||
      !!tagsFilter
    ) {
      setNameFilter(null);
      setRuleEngineTemplateNameFilter(null);
      setCoaTemplateNameFilter(null);
      setCreatedOnFilter(null);
      setCreatedUserNameFilter(null);
      setTagsFilter(null);
    }
  };

  const fetchData = async (
    currentPage: number,
    pageSize: number
  ): Promise<void> => {
    setIsLoading(true);
    try {
      // Construct filterObject
      const date = createdOnFilter ? new Date(createdOnFilter) : null;
      const formattedDate = date
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
            2,
            "0"
          )}-${String(date.getDate()).padStart(2, "0")}`
        : null;

      const filterObject: DeliverablesFilterParams = {
        name: nameFilter || "",
        ruleEngineTemplateId: ruleEngineTemplateNameFilter ?? 0,
        coaTemplateId: coaTemplateNameFilter ?? 0,
        createdOn: formattedDate ?? null,
        createdBy: createdUserNameFilter ?? DEFAULT_GUID,
        tagValue: tagsFilter ?? null,
        startIndex: currentPage - 1,
        itemsPerPage: pageSize,
        searchValue: searchValue ?? null,
      };

      const response = await deliverableService.getAllDeliverables<
        PagedResponse<Deliverable[]>
      >(filterObject);

      setDeliverables(response.items);
      setTotal(response.totalCount);
    } catch (error) {
      notification.error({ message: TOAST_DEFAULT_ERROR_MESSAGE });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFilterOptions = async (): Promise<void> => {
    try {
      const response: DeliverableFilter =
        await deliverableService.deliverablesFilter<DeliverableFilter>();

      setFilterOptions(response);

      const groupedTags: any = response.tagValueList.reduce(
        (acc: { [key: string]: string[] }, tag) => {
          if (!acc[tag.name]) {
            acc[tag.name] = [tag.value];
          } else {
            acc[tag.name].push(tag.value);
          }
          return acc;
        },
        {}
      );

      setTags(groupedTags);

      const options = Object.entries(groupedTags).flatMap(
        ([tagName, values]: any[]) => ({
          id:
            response.tagValueList.find((tag) => tag.name === tagName)?.id ?? 0,
          label: tagName,
          value: values.toString(),
        })
      );

      setTagOptions(options);
    } catch (error) {
      notification.error({ message: TOAST_DEFAULT_ERROR_MESSAGE });
    }
  };

  const openDeliverable = async (id: number) => {
    if (!id) return;
    try {
      const sheets = await deliverableService.getDeliverableMenuDetails<DeliverableMenuSheet[]>(id);
      if (sheets && sheets.length > 0) {
        const first = sheets[0];
        const deliverable = deliverables.find((d) => d.id === id);
        const name = deliverable?.name ?? "";
        let url = `/deliverable/${id}/${first.sheetId}?${ACTIVE_TABS_KEY}=${first.sheetName}&${NAME_KEY}=${name}`;
        if (first.multiSheet) {
          url += `&${MULTI_SHEET_KEY}=true`;
        }
        navigate(url);
      } else {
        navigate(`/deliverable/${id}`);
      }
    } catch {
      navigate(`/deliverable/${id}`);
    }
  };

  const Row = (props: any) => {
    const { children, ...restProps } = props;
    let id = restProps["data-row-key"];
    return (
      <tr
        {...restProps}
        onClick={() => openDeliverable(id)}
      >
        {children}
      </tr>
    );
  };

  return (
    <Container>
      <Breadcrumb
        className="breadCrump"
      />
      <div className="extra">
        <Flex gap="12px">
          <SearchInput
            onSearch={(value) => setSearchValue(value)}
            placeholder="Search Deliverables"
          />
          {/* <Button id="clear-filter" onClick={clearFilter}>{CLEAR_FILTER_TEXT}</Button> */}
        </Flex>
        <DeliverableCreate
          deliverableService={deliverableService}
          fetchData={fetchData}
          filterOptions={filterOptions}
          tagOptions={tagOptions}
          tags={tags}
        />
      </div>
      <Table
        columns={columns}
        dataSource={deliverables}
        isLoading={isLoading}
        fetchData={fetchData}
        total={total}
        row={Row}
      />
    </Container>
  );
};
